import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Building2, 
  Users, 
  CheckCircle, 
  Quote, 
  ArrowRight,
  Award,
  TrendingUp,
  Shield,
  Server,
  Phone
} from "lucide-react";
import { referencesAPI } from "../services/api";

const References = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        setLoading(true);
        const referencesData = await referencesAPI.getAll();
        setReferences(referencesData);
      } catch (error) {
        console.error('Error fetching references:', error);
        setError('Fehler beim Laden der Referenzen');
      } finally {
        setLoading(false);
      }
    };

    fetchReferences();
  }, []);

  const stats = [
    {
      icon: Users,
      value: "500+",
      label: "Zufriedene Kunden",
      color: "blue"
    },
    {
      icon: Building2,
      value: "200+",
      label: "Erfolgreich abgeschlossene Projekte",
      color: "green"
    },
    {
      icon: Award,
      value: "15+",
      label: "Jahre Erfahrung",
      color: "purple"
    },
    {
      icon: TrendingUp,
      value: "98%",
      label: "Kundenzufriedenheit",
      color: "orange"
    }
  ];

  const industries = [
    {
      name: "Maschinenbau",
      projects: 45,
      description: "Spezialisierte IT-Lösungen für Produktionsbetriebe und Engineering",
      icon: "🏭"
    },
    {
      name: "Software-Entwicklung",
      projects: 38,
      description: "High-Performance IT-Infrastrukturen für Tech-Unternehmen",
      icon: "💻"
    },
    {
      name: "Gesundheitswesen",
      projects: 32,
      description: "DSGVO-konforme IT-Systeme für Arztpraxen und Kliniken",
      icon: "🏥"
    },
    {
      name: "Finanzdienstleistungen",
      projects: 28,
      description: "Hochsichere IT-Umgebungen für Banken und Versicherungen",
      icon: "🏦"
    },
    {
      name: "Handel & E-Commerce",
      projects: 35,
      description: "Skalierbare Online-Plattformen und POS-Systeme",
      icon: "🛒"
    },
    {
      name: "Beratung & Services",
      projects: 42,
      description: "Flexible IT-Lösungen für Dienstleistungsunternehmen",
      icon: "💼"
    }
  ];

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge className="bg-green-100 text-green-800">Referenzen & Projekte</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Referenzen
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Über 500 zufriedene Kunden vertrauen auf unsere IT-Expertise. 
            Hier ein <strong>Auszug</strong> unserer Projekte und Partnerschaften.
          </p>
        </div>

        {/* Stats */}
        <section className="mb-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center p-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                  stat.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                  stat.color === 'green' ? 'bg-green-100 text-green-600' :
                  stat.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                  'bg-orange-100 text-orange-600'
                }`}>
                  <stat.icon className="h-8 w-8" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </Card>
            ))}
          </div>
        </section>

        {/* Success Stories */}
        <section className="mb-20">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Erfolgsgeschichten unserer Kunden
            </h2>
            <p className="text-lg text-gray-600">
              Wie wir Unternehmen bei ihrer digitalen Transformation unterstützen
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-lg">Erfolgsgeschichten werden geladen...</div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">{error}</div>
              <Button onClick={() => window.location.reload()}>
                Erneut versuchen
              </Button>
            </div>
          ) : (
            <div className="space-y-12">
              {references.map((story, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className="grid lg:grid-cols-2 gap-0">
                    <div className="p-8">
                      <Badge className="mb-4">{story.industry}</Badge>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{story.project} bei {story.company}</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-blue-600 mb-2">Projekt:</h4>
                          <p className="text-gray-600">{story.description}</p>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-green-600 mb-3">Ergebnisse:</h4>
                          <ul className="space-y-2">
                            {story.results.map((result, idx) => (
                              <li key={idx} className="flex items-center">
                                <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                                <span className="text-gray-700">{result}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className="lg:h-full">
                      <img
                        src={story.image || "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxJVCUyMGluZnJhc3RydWN0dXJlfGVufDB8fHx8MTc1ODE2NzQzM3ww&ixlib=rb-4.1.0&q=85"}
                        alt={story.project}
                        className="w-full h-64 lg:h-full object-cover"
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Customer Testimonials */}
        <section className="mb-20 bg-gray-50 p-8 rounded-2xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Was unsere Kunden sagen
            </h2>
            <p className="text-gray-600">
              Auszug aus unseren Kundenbewertungen
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-lg">Referenzen werden geladen...</div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">{error}</div>
              <Button onClick={() => window.location.reload()}>
                Erneut versuchen
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {references.map((reference) => (
              <Card key={reference.id} className="relative">
                <CardHeader>
                  <Quote className="h-8 w-8 text-blue-600 mb-4" />
                  <CardDescription className="text-lg italic text-gray-700">
                    "{reference.testimonial}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-t pt-4">
                    <div className="font-semibold text-gray-900">{reference.contact}</div>
                    <div className="text-sm text-gray-600">{reference.company}</div>
                    <Badge variant="outline" className="mt-2">{reference.industry}</Badge>
                  </div>
                  
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">Projekt: {reference.project}</h4>
                    <p className="text-sm text-gray-600 mb-3">{reference.description}</p>
                    <div className="space-y-1">
                      {reference.results.map((result, idx) => (
                        <div key={idx} className="flex items-center text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                          {result}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
              ))}
            </div>
          )}
        </section>

        {/* Industries */}
        <section className="mb-20">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Branchen-Expertise
            </h2>
            <p className="text-lg text-gray-600">
              Wir verstehen die spezifischen IT-Anforderungen verschiedener Branchen
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <div className="text-4xl mb-3">{industry.icon}</div>
                  <CardTitle className="text-lg">{industry.name}</CardTitle>
                  <CardDescription>{industry.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Abgeschlossene Projekte:</span>
                    <Badge className="bg-blue-100 text-blue-800">{industry.projects}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center space-y-8 bg-blue-600 p-12 rounded-2xl">
          <h2 className="text-3xl font-bold text-white">
            Werden Sie unser nächster Erfolgsfall
          </h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Lassen Sie uns gemeinsam Ihr IT-Projekt zum Erfolg führen. 
            Kontaktieren Sie uns für eine kostenlose Projektberatung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contact">
                <Phone className="mr-2 h-5 w-5" />
                Projekt besprechen
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-blue-600" 
              asChild
            >
              <Link to="/services">
                Unsere Leistungen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default References;